<template>
  <div  v-if="event && nodeType === 'event'" id="EventInfoBlock" class="EventInfoBlock">
    <div class="EventInfoBlock__container">
      <h2 class="EventInfoBlock__heading">{{ translations.information }}</h2>
      <ul class="EventInfoBlock__details">
        <li v-if="event.tags.location" class="EventInfoBlock__item">
          <div class="EventInfoBlock__label">{{ translations.location }}</div>
          <div class="EventInfoBlock__text">
            <span
              v-for="(tag, index) in event.tags.location"
              :key="`location-${index}`"
            >
              {{ tag.title
              }}<span v-if="index !== event.tags.location.length - 1">, </span>
            </span>
          </div>
        </li>
        <li v-if="event.startDate" class="EventInfoBlock__item">
          <div class="EventInfoBlock__label">{{ translations.date }}</div>
          <div class="EventInfoBlock__text">
            <span>{{ formatDate(event.startDate) }}</span>
            <span v-if="event.endDate">- {{ formatDate(event.endDate) }}</span>
          </div>
        </li>
        <li v-if="event.duration" class="EventInfoBlock__item">
          <div class="EventInfoBlock__label">{{ translations.duration }}</div>
          <div class="EventInfoBlock__text">
            {{ event.duration }}
          </div>
        </li>
        <li
          v-if="event.tags.targetAudience.length"
          class="EventInfoBlock__item"
        >
          <div class="EventInfoBlock__label">
            {{ translations.targetAudience }}
          </div>
          <div class="EventInfoBlock__text">
            <span
              v-for="(tag, index) in event.tags.targetAudience"
              :key="`targetAudience-${index}`"
            >
              {{ tag.title
              }}<span v-if="index !== event.tags.targetAudience.length - 1"
                >,</span
              >
            </span>
          </div>
        </li>
        <li v-if="event.tags.accessibility.length" class="EventInfoBlock__item">
          <div class="EventInfoBlock__label">
            {{ translations.accessibility }}
          </div>
          <div class="EventInfoBlock__text">
            <span
              v-for="(tag, index) in event.tags.accessibility"
              :key="`accessibility-${index}`"
            >
              {{ tag.title
              }}<span v-if="index !== event.tags.accessibility.length - 1"
                >, </span
              >
            </span>
          </div>
        </li>
        <li v-if="event.tags.effects && event.tags.effects.length" class="EventInfoBlock__item">
          <div class="EventInfoBlock__label">
            {{ translations.effects }}
          </div>
          <div class="EventInfoBlock__text">
            <span
              v-for="(tag, index) in event.tags.effects"
              :key="`effects-${index}`"
            >
              {{ tag.title
              }}<span v-if="index !== event.tags.effects.length - 1">, </span>
            </span>
          </div>
        </li>
        <li v-if="event.price" class="EventInfoBlock__item">
          <div class="EventInfoBlock__label">{{ translations.price }}</div>
          <div class="EventInfoBlock__text">
            {{ event.price }}
          </div>
        </li>
      </ul>
    </div>
    <a
      v-if="showButton && !event.hidePriceInfo"
      class="button button--large EventInfoBlock__cta"
      href="#EventTicketsBlock"
      @click="scrollToTickets"
    >
      <svg
        class="icon"
        fill="none"
        height="32"
        viewBox="0 0 32 32"
        width="32"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          clip-rule="evenodd"
          d="M22.9394 3.63645C24.5795 3.63645 25.9091 2.30687 25.9091 0.666748H28.8788C30.5189 0.666748 31.8485 1.99633 31.8485 3.63645V24.4243C31.8485 26.0644 30.5189 27.394 28.8788 27.394H17C15.3599 27.394 14.0303 26.0644 14.0303 24.4243V3.63645C14.0303 1.99633 15.3599 0.666748 17 0.666748H19.9697C19.9697 2.30687 21.2993 3.63645 22.9394 3.63645ZM19.9697 8.09099C19.9697 8.91105 19.3049 9.57584 18.4848 9.57584C17.6648 9.57584 17 8.91105 17 8.09099C17 7.27093 17.6648 6.60614 18.4848 6.60614C19.3049 6.60614 19.9697 7.27093 19.9697 8.09099ZM22.9394 9.57584C23.7594 9.57584 24.4242 8.91105 24.4242 8.09099C24.4242 7.27093 23.7594 6.60614 22.9394 6.60614C22.1193 6.60614 21.4545 7.27093 21.4545 8.09099C21.4545 8.91105 22.1193 9.57584 22.9394 9.57584ZM28.8788 8.09099C28.8788 8.91105 28.214 9.57584 27.3939 9.57584C26.5739 9.57584 25.9091 8.91105 25.9091 8.09099C25.9091 7.27093 26.5739 6.60614 27.3939 6.60614C28.214 6.60614 28.8788 7.27093 28.8788 8.09099Z"
          fill="white"
          fill-rule="evenodd"
        />
        <path
          clip-rule="evenodd"
          d="M12.041 3.63645C12.2046 3.54198 12.3735 3.46486 12.5455 3.4045V24.4243C12.5455 26.8845 14.5398 28.8789 17 28.8789H21.7744L15.1173 32.7223C13.6969 33.5424 11.8807 33.0557 11.0606 31.6354L0.666663 13.6325C-0.153397 12.2121 0.333263 10.3959 1.75365 9.57584L4.32548 8.09099C5.14554 9.51137 6.96178 9.99803 8.38216 9.17798C9.80255 8.35792 10.2892 6.54168 9.46915 5.12129L12.041 3.63645ZM7.49411 16.5489C8.2043 16.1389 8.44763 15.2308 8.0376 14.5206C7.62757 13.8104 6.71945 13.5671 6.00926 13.9771C5.29907 14.3871 5.05574 15.2952 5.46577 16.0054C5.8758 16.7156 6.78392 16.9589 7.49411 16.5489ZM11.8954 12.2933C12.3054 13.0035 12.0621 13.9116 11.3519 14.3216C10.6417 14.7317 9.73355 14.4883 9.32352 13.7781C8.91349 13.068 9.15682 12.1598 9.86701 11.7498C10.5772 11.3398 11.4853 11.5831 11.8954 12.2933Z"
          fill="white"
          fill-rule="evenodd"
        />
      </svg>
      {{ translations.tickets }}
    </a>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "EventInfoBlock",
  data() {
    return {
      nodeType:drupalSettings.node_type,
      translations: {
        information: Drupal.t("Information"),
        tickets: Drupal.t("Tickets"),
        location: Drupal.t("Location"),
        date: Drupal.t("Date"),
        duration: Drupal.t("Duration"),
        targetAudience: Drupal.t("Target Audience"),
        accessibility: Drupal.t("Accessibility"),
        effects: Drupal.t("Effects"),
        price: Drupal.t("Price"),
        brake: Drupal.t("brake"),
        noBrake: Drupal.t("no brake"),
      },
    };
  },
  mounted() {
    if (drupalSettings.nid) {
      this.fetchEventData(drupalSettings.nid);
    }
  },
  methods: {
    ...mapActions(["fetchEventData", "openModal"]),
    formatDate(date) {
      let dateObject = new Date(date);
      return (
        dateObject.toLocaleDateString("sv-SE", { day: "numeric" }) +
        " " +
        dateObject.toLocaleDateString("sv-SE", { month: "long" }) +
        " " +
        dateObject.toLocaleDateString("sv-SE", { year: "numeric" })
      );
    },
    timeConvert(time) {
      let hours = time / 100;
      hours = Math.floor(hours);
      let minutes = time % 100;
      if (minutes > 0) {
        return hours + ":" + minutes;
      } else {
        return hours;
      }
    },
    scrollToTickets(event) {
      event.preventDefault(); // Prevent default anchor behavior
      const headerOffset = document.querySelector('header').offsetHeight; // Get the height of your fixed header
      const targetElement = document.querySelector('#EventTicketsBlock');
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }

  },
  computed: {
    ...mapGetters({
      eventData: "getEventData",
      showButton: "getTicketButtonState",
    }),
    event() {
      return this.eventData[drupalSettings.nid];
    },
  },
};
</script>

<style lang="scss">
@import "web/themes/custom/khst_theme/_sass-config/sass-config";

.EventInfoBlock {
  @include breakpoint($desktop-large) {
    left: calc(50% + 430px);
    margin-right: 5px;
    position: fixed;
    top: 40vh;
    z-index: 10;

    .toolbar-fixed & {
      margin-left: 40px;
    }
    .toolbar-fixed[data-toolbar-menu="open"] & {
      margin-left: 120px;
    }
  }
}

.EventInfoBlock__container {
  background: black;
  color: white;
  min-height: 100px;
  min-width: 270px;
  padding: 12px 15px 20px 50px;
  position: relative;
  width: auto;
  @include breakpoint($tablet) {
    padding: 15px 50px 10px;
  }
  @include breakpoint($desktop-large) {
    margin-bottom: 5px;
    min-height: 125px;
    padding: 9px 20px 10px 38px;
    width: 300px;
  }
}

.EventInfoBlock__heading {
  font-family: $neue-plak-cond;
  font-size: 20px;
  font-weight: 700;
  left: -15px;
  line-height: 20px;
  margin: 0;
  position: absolute;
  text-transform: uppercase;
  top: 48px;
  transform: rotate(90deg);

  @include breakpoint($tablet) {
    font-weight: 900;
    top: 53px;
  }
  @include breakpoint($desktop-large) {
    left: -22px;
    top: 48px;
  }
}

html:lang(en) .EventInfoBlock__heading {
  @include breakpoint($desktop-large) {
    left: -40px;
    top: 68px;
  }
}

.EventInfoBlock__details {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  list-style: none;
  margin: 0 auto;
  max-width: 688px;
  padding: 0;
  @include breakpoint($tablet) {
    height: 200px;
  }
  @include breakpoint($desktop-large) {
    height: auto;
  }
}

.EventInfoBlock__item {
  margin-bottom: 10px;
}

.EventInfoBlock__label {
  font-family: $neue-plak-cond;
  font-size: 16px;
  font-weight: 700;
  line-height: 18px;
  text-transform: uppercase;
  @include breakpoint($tablet) {
    font-size: 20px;
    line-height: 22px;
  }
}

.EventInfoBlock__text {
  font-size: 16px;
  line-height: 22px;
}

.EventInfoBlock__cta {
  background: $red;
  bottom: 0;
  color: white;
  display: block;
  left: 0;
  margin: 0;
  position: fixed;
  width: 100%;
  z-index: 10;

  .icon {
    margin-bottom: -5px;
    margin-right: 10px;
  }

  @include breakpoint($desktop-large) {
    bottom: auto;
    position: static;
  }
}
</style>
